/**
 * PRD.307 — WhatsApp booking QR: pure helpers.
 *
 * Deliberately free of Vite/React imports so the resolution rule, the download
 * filename and the print sheet can be unit-tested with node:test. The Vite glob
 * that supplies the asset map lives in `src/clients/whatsapp-qr-registry.ts`.
 */

/** Registry shape: client id → bundled asset URL, as produced by import.meta.glob. */
export type WhatsappQrAssetMap = Record<string, string>;

/**
 * Pick a client's QR from the asset map.
 *
 * Returns null for an unknown/blank client id or a client with no asset — the
 * caller hides the section rather than rendering a broken image (FR3). WhatsApp
 * availability is decided ONLY by the presence of this asset, never inferred
 * from a clinic phone number.
 */
export function pickWhatsappQr(
  assets: WhatsappQrAssetMap,
  clientId: string | undefined | null,
): string | null {
  const id = (clientId ?? '').trim();
  if (!id) return null;
  return assets[id] ?? null;
}

/**
 * Download filename: `<org>-whatsapp-qr.png` (FR5).
 *
 * The client id comes from build config rather than user input, but it lands in
 * a Content-Disposition-ish filename, so path separators and spaces are
 * normalized out instead of trusted.
 */
export function whatsappQrFilename(clientId: string | undefined | null): string {
  const slug = (clientId ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `${slug}-whatsapp-qr.png` : 'whatsapp-qr.png';
}

/** Escape text interpolated into the print sheet's HTML. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export interface WhatsappQrPrintInput {
  /** Clinic display name shown as the sheet's identity. */
  clinicName: string;
  /** Bundled QR asset URL — printed as-is, never re-encoded (FR7). */
  qrUrl: string;
  /** Optional clinic logo URL; omitted from the sheet when absent. */
  logoUrl?: string | null;
}

/**
 * Build the patient-facing print sheet's `<body>` markup for
 * `printHtmlViaIframe`, which renders it in an isolated iframe — so the admin
 * UI is never part of the printout (FR6).
 *
 * The QR is an `<img>` pointing at the original PNG at a fixed 60mm square with
 * generous surrounding whitespace: no cropping, no overlay, no rasterization of
 * the card, and the quiet zone preserved (FR4/FR7).
 */
export function buildWhatsappQrPrintHtml(input: WhatsappQrPrintInput): string {
  const name = escapeHtml(input.clinicName.trim());
  const qr = escapeHtml(input.qrUrl);
  const logo = input.logoUrl?.trim() ? escapeHtml(input.logoUrl.trim()) : null;

  return [
    '<div class="wa-qr-sheet" style="text-align:center;font-family:Arial,Helvetica,sans-serif;padding:24mm 16mm;">',
    logo
      ? `<img src="${logo}" alt="" style="max-height:22mm;max-width:70mm;object-fit:contain;margin-bottom:8mm;" />`
      : '',
    name ? `<div style="font-size:20pt;font-weight:700;margin-bottom:4mm;">${name}</div>` : '',
    '<div style="font-size:16pt;font-weight:600;margin-bottom:10mm;">Scan to book on WhatsApp</div>',
    // Square box + object-fit:contain keeps the aspect ratio whatever the
    // source dimensions are; width/height in mm gives a reliable scan size.
    `<img src="${qr}" alt="WhatsApp booking QR code${name ? ` for ${name}` : ''}"`,
    ' style="width:60mm;height:60mm;object-fit:contain;display:block;margin:0 auto;" />',
    '<div style="font-size:11pt;margin-top:10mm;max-width:110mm;margin-left:auto;margin-right:auto;line-height:1.5;">',
    'Scan this QR code with your phone to start a WhatsApp conversation with the clinic.',
    '</div>',
    '</div>',
  ].join('');
}
